import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, InputLabel,
  MenuItem, Select, Slider, TextField, Typography,
} from "@mui/material";
import { CheckCircle, Delete, Edit, Lock, Person, PhotoCamera, Save } from "@mui/icons-material";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { api } from "api/config";
import { setUser, user as getUser, setDefaultStation as persistDefaultStation } from "localStorage";
import OTPInput from "components/OTPInput";
import { generateUniqueId } from "utils";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import { DIVIDER } from "components/bento/bentoTokens";

const userApi = api();

const MAX_OUTPUT_PX = 512;
const OUTPUT_QUALITY = 0.82;

const getCroppedImg = (imageSrc, pixelCrop) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const size = Math.min(pixelCrop.width, pixelCrop.height, MAX_OUTPUT_PX);
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      canvas.getContext("2d").drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, size, size
      );
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", OUTPUT_QUALITY);
    };
    image.onerror = reject;
    image.src = imageSrc;
  });

const WA_STEPS = ["Verify Password", "New Number", "Enter OTP"];

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [defaultStation, setDefaultStation] = useState("");
  const [defaultWhatsappInstance, setDefaultWhatsappInstance] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Profile picture
  const [previewUrl, setPreviewUrl] = useState(null);
  const [picFile, setPicFile] = useState(null);
  const fileInputRef = useRef();

  // Crop dialog
  const [rawImageSrc, setRawImageSrc] = useState(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropApplying, setCropApplying] = useState(false);

  // Delete picture dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // WhatsApp update dialog
  const [waOpen, setWaOpen] = useState(false);
  const [waStep, setWaStep] = useState(0);
  const [waPassword, setWaPassword] = useState("");
  const [waNewNumber, setWaNewNumber] = useState("");
  const [waOtp, setWaOtp] = useState(new Array(6).fill(""));
  const [waRef, setWaRef] = useState("");
  const [waLoading, setWaLoading] = useState(false);

  const openWaDialog = () => {
    setWaOpen(true); setWaStep(0);
    setWaPassword(""); setWaNewNumber("");
    setWaOtp(new Array(6).fill("")); setWaRef("");
  };
  const closeWaDialog = () => setWaOpen(false);

  useEffect(() => {
    userApi.get("/user/me/profile")
      .then((res) => {
        if (res.data?.success) {
          const u = res.data.data;
          setProfile(u);
          setName(u.name || "");
          const def = u.stations?.find((s) => s.default);
          setDefaultStation(def?.id || "");
          const defWa = u.whatsappInstances?.find((w) => w.default);
          setDefaultWhatsappInstance(defWa?.id || "");
        }
      })
      .catch(() => toast.error("Failed to load profile"))
      .finally(() => setLoading(false));
  }, []);

  // ── WhatsApp update flow ────────────────────────────────────────────────────
  const handleWaStep1 = () => {
    if (!waPassword) { toast.error("Please enter your current password"); return; }
    setWaStep(1);
  };

  const handleWaStep2 = async () => {
    const clean = waNewNumber.replace(/\D/g, "");
    if (clean.length < 8) { toast.error("Please enter a valid WhatsApp number"); return; }
    if (clean === String(profile?.whatsapp)) { toast.error("New number must be different from current number"); return; }

    setWaLoading(true);
    try {
      // Check availability
      await userApi.post("/user/me/check-whatsapp", { whatsapp: clean });

      // Send OTP
      const ref = generateUniqueId(10);
      const otpRes = await userApi.post("/public/otp-request", {
        phone: clean, ref, otpType: "staff-whatsapp-update",
      });

      if (otpRes.data?.result === "success" || otpRes.data?.result === "partial_success") {
        setWaRef(ref);
        setWaOtp(new Array(6).fill(""));
        setWaStep(2);
        toast.info("Verification code sent to your new WhatsApp number");
      } else {
        toast.error(otpRes.data?.message || "Failed to send verification code");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to verify number");
    } finally {
      setWaLoading(false);
    }
  };

  const handleWaSubmit = async () => {
    const otpStr = waOtp.join("");
    if (otpStr.length < 6) { toast.error("Please enter the complete verification code"); return; }

    setWaLoading(true);
    try {
      const clean = waNewNumber.replace(/\D/g, "");
      const res = await userApi.post("/user/me/update-whatsapp", {
        currentPassword: waPassword,
        newWhatsapp: clean,
        otp: otpStr,
        ref: waRef,
      });

      if (res.data?.success) {
        setProfile(res.data.data);
        toast.success("WhatsApp number updated successfully");
        closeWaDialog();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update WhatsApp number");
    } finally {
      setWaLoading(false);
    }
  };

  // ── Profile picture ─────────────────────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 20 * 1024 * 1024) { toast.error("Image must be less than 20MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawImageSrc(ev.target.result);
      setCrop({ x: 0, y: 0 }); setZoom(1);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_, pixels) => setCroppedAreaPixels(pixels), []);

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels) return;
    setCropApplying(true);
    try {
      const blob = await getCroppedImg(rawImageSrc, croppedAreaPixels);
      setPicFile(new File([blob], "profile.jpg", { type: "image/jpeg" }));
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(blob);
      setCropDialogOpen(false); setRawImageSrc(null);
    } catch { toast.error("Failed to crop image"); }
    finally { setCropApplying(false); }
  };

  const handleDeletePicture = async () => {
    setDeleting(true);
    try {
      const formData = new FormData();
      formData.append("deletePicture", "true");
      const res = await userApi.patch("/user/me/profile", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.success) {
        setProfile((p) => ({ ...p, profilePicture: null }));
        setPreviewUrl(null); setPicFile(null);
        const cur = getUser();
        if (cur) { setUser({ ...cur, profilePicture: null }); window.dispatchEvent(new Event("storage")); }
        toast.success("Profile picture removed");
      }
    } catch { toast.error("Failed to remove profile picture"); }
    finally { setDeleting(false); setDeleteDialogOpen(false); }
  };

  // ── Save profile ────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (newPassword && newPassword !== confirmPassword) { toast.error("New passwords do not match"); return; }
    if (newPassword && newPassword.length < 6) { toast.error("New password must be at least 6 characters"); return; }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      if (defaultStation) formData.append("defaultStation", defaultStation);
      if (defaultWhatsappInstance) formData.append("defaultWhatsappInstance", defaultWhatsappInstance);
      if (newPassword) { formData.append("currentPassword", currentPassword); formData.append("newPassword", newPassword); }
      if (picFile) formData.append("profilePicture", picFile);

      const res = await userApi.patch("/user/me/profile", formData, { headers: { "Content-Type": "multipart/form-data" } });
      if (res.data?.success) {
        const updated = res.data.data;
        setProfile(updated);
        setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); setPicFile(null);
        const cur = getUser();
        if (cur) { setUser({ ...cur, name: updated.name, profilePicture: updated.profilePicture }); window.dispatchEvent(new Event("storage")); }
        // Sync defaultStation in localStorage so Scan page picks it up
        if (defaultStation) {
          const stationObj = updated.stations?.find((s) => s.id === defaultStation);
          if (stationObj) persistDefaultStation(stationObj);
        }
        toast.success("Profile updated successfully");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update profile");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress color="secondary" /></Box>;
  }

  const avatarSrc = previewUrl || profile?.profilePicture || null;
  const hasPicture = !!(previewUrl || profile?.profilePicture);

  return (
    <PageShell title="My Profile" sx={{ maxWidth: 760 }}>
      {/* Avatar card */}
      <BentoCard sx={{ mb: 3 }} bodySx={{ p: 3, display: "flex", alignItems: "center", gap: 3 }}>
        <Box position="relative">
          <Avatar src={avatarSrc} sx={{ width: 88, height: 88, fontSize: "2rem" }}>
            {!avatarSrc && name.charAt(0).toUpperCase()}
          </Avatar>
          <Box onClick={() => fileInputRef.current.click()} sx={{
            position: "absolute", bottom: 0, right: hasPicture ? 28 : 0,
            bgcolor: "secondary.main", borderRadius: "50%", width: 26, height: 26,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: 2,
          }}>
            <PhotoCamera sx={{ fontSize: 14, color: "white" }} />
          </Box>
          {hasPicture && (
            <Box onClick={() => setDeleteDialogOpen(true)} sx={{
              position: "absolute", bottom: 0, right: 0,
              bgcolor: "error.main", borderRadius: "50%", width: 26, height: 26,
              display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: 2,
            }}>
              <Delete sx={{ fontSize: 14, color: "white" }} />
            </Box>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect} />
        </Box>
        <Box flex={1}>
          <Typography variant="h6" fontWeight="bold">{profile?.name}</Typography>
          <Chip label={profile?.role} size="small" color="secondary" sx={{ mt: 0.5 }} />
          {profile?.agentId && <Typography variant="body2" color="text.secondary" mt={0.5}>Agent ID: {profile.agentId}</Typography>}
          {picFile && <Typography variant="caption" color="success.main" display="block" mt={0.5}>New picture selected — save to apply</Typography>}
        </Box>
      </BentoCard>

      {/* Personal Info */}
      <BentoCard sx={{ mb: 3 }} bodySx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Person color="secondary" />
          <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth color="secondary" />
          <TextField label="Email" value={profile?.email || ""} fullWidth disabled helperText="Email cannot be changed" />
          <Box display="flex" alignItems="center" gap={1}>
            <TextField label="WhatsApp Number" value={profile?.whatsapp || ""} fullWidth disabled
              helperText="Click the button to update your WhatsApp number" />
            <Button variant="outlined" color="secondary" startIcon={<Edit />} onClick={openWaDialog}
              sx={{ whiteSpace: "nowrap", minWidth: 120, alignSelf: "flex-start", mt: "2px" }}>
              Update
            </Button>
          </Box>
        </Box>
      </BentoCard>

      {/* Default Station */}
      {profile?.stations?.length > 0 && (
        <BentoCard sx={{ mb: 3 }} bodySx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Default Station</Typography>
          <FormControl fullWidth color="secondary">
            <InputLabel>Default Station</InputLabel>
            <Select value={defaultStation} onChange={(e) => setDefaultStation(e.target.value)} label="Default Station">
              {profile.stations.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </BentoCard>
      )}

      {/* Default WhatsApp Instance — only worth a picker once there's an actual choice; with
          0 or 1 assigned instance the "WhatsApp Chat" menu has nothing to pick between. */}
      {profile?.whatsappInstances?.length >= 2 && (
        <BentoCard sx={{ mb: 3 }} bodySx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="bold" mb={2}>Default WhatsApp Instance</Typography>
          <FormControl fullWidth color="secondary">
            <InputLabel>Default WhatsApp Instance</InputLabel>
            <Select value={defaultWhatsappInstance} onChange={(e) => setDefaultWhatsappInstance(e.target.value)} label="Default WhatsApp Instance">
              {profile.whatsappInstances.map((w) => (
                <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </BentoCard>
      )}

      {/* Change Password */}
      <BentoCard sx={{ mb: 3 }} bodySx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Lock color="secondary" />
          <Typography variant="h6" fontWeight="bold">Change Password</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>Leave blank to keep your current password.</Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField label="Current Password" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} fullWidth color="secondary" />
          <TextField label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} fullWidth color="secondary" />
          <TextField label="Confirm New Password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} fullWidth color="secondary"
            error={confirmPassword.length > 0 && newPassword !== confirmPassword}
            helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? "Passwords do not match" : ""} />
        </Box>
      </BentoCard>

      <Divider sx={{ mb: 2, borderColor: DIVIDER }} />
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="secondary" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Box>

      {/* ── WhatsApp Update Dialog ── */}
      <Dialog open={waOpen} onClose={closeWaDialog} maxWidth="xs" fullWidth sx={{ "& .MuiDialog-paper": { borderRadius: 3 } }}>
        <DialogTitle>
          <Typography fontWeight={600}>Update WhatsApp Number</Typography>
          <Typography variant="caption" color="text.secondary">
            Step {waStep + 1} of {WA_STEPS.length} — {WA_STEPS[waStep]}
          </Typography>
        </DialogTitle>

        <DialogContent>
          {/* Step 0: verify password */}
          {waStep === 0 && (
            <Box pt={1}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter your current password to confirm your identity.
              </Typography>
              <TextField label="Current Password" type="password" value={waPassword}
                onChange={(e) => setWaPassword(e.target.value)} fullWidth color="secondary"
                onKeyDown={(e) => e.key === "Enter" && handleWaStep1()} />
            </Box>
          )}

          {/* Step 1: enter new number */}
          {waStep === 1 && (
            <Box pt={1}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Enter your new WhatsApp number. An OTP will be sent to verify it.
              </Typography>
              <TextField label="New WhatsApp Number" value={waNewNumber} onChange={(e) => setWaNewNumber(e.target.value)}
                fullWidth color="secondary" placeholder="e.g. 8562012345678"
                helperText="Include country code, digits only"
                onKeyDown={(e) => e.key === "Enter" && handleWaStep2()} />
            </Box>
          )}

          {/* Step 2: OTP */}
          {waStep === 2 && (
            <Box pt={1} textAlign="center">
              <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="body2" color="text.secondary" mb={3}>
                Enter the 6-digit code sent to <strong>+{waNewNumber.replace(/\D/g, "")}</strong>
              </Typography>
              <OTPInput otp={waOtp} setOtp={setWaOtp} length={6} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={waStep === 0 ? closeWaDialog : () => setWaStep((s) => s - 1)}
            variant="outlined" color="secondary" disabled={waLoading}>
            {waStep === 0 ? "Cancel" : "Back"}
          </Button>

          {waStep === 0 && (
            <Button onClick={handleWaStep1} variant="contained" color="secondary" disabled={waLoading}>Next</Button>
          )}
          {waStep === 1 && (
            <Button onClick={handleWaStep2} variant="contained" color="secondary" disabled={waLoading}
              startIcon={waLoading ? <CircularProgress size={16} color="inherit" /> : null}>
              {waLoading ? "Sending..." : "Send OTP"}
            </Button>
          )}
          {waStep === 2 && (
            <Button onClick={handleWaSubmit} variant="contained" color="secondary" disabled={waLoading}
              startIcon={waLoading ? <CircularProgress size={16} color="inherit" /> : null}>
              {waLoading ? "Updating..." : "Confirm"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Crop Dialog ── */}
      <Dialog open={cropDialogOpen} onClose={() => { setCropDialogOpen(false); setRawImageSrc(null); }} maxWidth="sm" fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography fontWeight={600} fontSize="1.1rem">Crop Profile Picture</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>Drag to reposition · Scroll or use slider to zoom</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, pb: "0 !important" }}>
          <Box sx={{ position: "relative", width: "100%", height: 360, background: "#111" }}>
            {rawImageSrc && (
              <Cropper image={rawImageSrc} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
                onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onCropComplete} />
            )}
          </Box>
          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <Typography variant="caption" color="text.secondary">Zoom</Typography>
            <Slider value={zoom} min={1} max={3} step={0.05} onChange={(_, v) => setZoom(v)} color="secondary" size="small" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => { setCropDialogOpen(false); setRawImageSrc(null); }} variant="outlined" color="secondary" disabled={cropApplying}>Cancel</Button>
          <Button onClick={handleApplyCrop} variant="contained" color="secondary" disabled={cropApplying}>
            {cropApplying ? <CircularProgress size={20} color="inherit" /> : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Picture Confirmation ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center" }}>
          <Delete color="error" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6">Remove Profile Picture?</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} variant="outlined" color="secondary" disabled={deleting}>Cancel</Button>
          <Button onClick={handleDeletePicture} variant="contained" color="error" disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}>
            {deleting ? "Removing..." : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ProfilePage;
